import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Shield, Users, BarChart2, Search, X, ChevronLeft, ChevronRight,
  Crown, Zap, Building2, Star, Edit3, Trash2, RefreshCw, AlertTriangle,
  CheckCircle, Loader2, Eye, Copy, ExternalLink, Mail, Calendar,
  TrendingUp, Activity, Database, Key, LogOut, Scan, UserCheck,
  Ban, RotateCcw, Filter, Download, ArrowUpRight, Clock, Hash,
  ChevronDown, Save, XCircle, Info, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'erik.datum@gmail.com';

// ─── Toast ─────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div className="fixed top-5 right-5 z-[200] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, x: 60, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60, scale: 0.9 }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-2xl font-mono text-sm pointer-events-auto ${
              t.type === 'error' ? 'bg-red-950 border-red-500/40 text-red-300' :
              t.type === 'warn'  ? 'bg-amber-950 border-amber-500/40 text-amber-300' :
                                   'bg-emerald-950 border-emerald-500/40 text-emerald-300'
            }`}>
            {t.type === 'error' ? <AlertTriangle className="w-4 h-4" /> :
             t.type === 'warn'  ? <Info className="w-4 h-4" /> :
             <CheckCircle className="w-4 h-4" />}
            {t.msg}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color = 'blue', trend }) {
  const colors = {
    blue:    'from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-400',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    purple:  'from-purple-500/10 to-purple-500/5 border-purple-500/20 text-purple-400',
    amber:   'from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-400',
    red:     'from-red-500/10 to-red-500/5 border-red-500/20 text-red-400',
    zinc:    'from-zinc-800 to-zinc-800/50 border-zinc-700 text-zinc-400',
  };
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colors[color]} border rounded-2xl p-5 relative overflow-hidden`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-1 text-xs font-mono ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            <ArrowUpRight className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend)}
          </span>
        )}
      </div>
      <p className="font-heading text-3xl font-black text-white mb-1">{value}</p>
      <p className="text-zinc-500 font-mono text-xs uppercase tracking-wider">{label}</p>
      {sub && <p className="text-zinc-600 font-mono text-xs mt-1">{sub}</p>}
    </motion.div>
  );
}

// ─── Plan Badge ────────────────────────────────────────────────────
function PlanBadge({ plan }) {
  const map = {
    business: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    pro:      'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
    free:     'bg-zinc-800 text-zinc-400 border-zinc-700',
  };
  const icons = {
    business: <Building2 className="w-3 h-3" />,
    pro:      <Star className="w-3 h-3" />,
    free:     <Zap className="w-3 h-3" />,
  };
  const p = plan || 'free';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-bold border ${map[p] || map.free}`}>
      {icons[p] || icons.free} {p.charAt(0).toUpperCase() + p.slice(1)}
    </span>
  );
}

// ─── User Row ──────────────────────────────────────────────────────
function UserRow({ user, onSelect, onPlanChange, onDelete, saving }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const plans = ['free', 'pro', 'business'];

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors group"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-mono text-xs font-bold flex-shrink-0">
            {(user.name || 'U')[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-mono text-sm font-bold truncate max-w-[140px]">{user.name || '—'}</p>
            <p className="text-zinc-500 font-mono text-xs truncate max-w-[140px]">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="relative">
          <button onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
            <PlanBadge plan={user.plan} />
            <ChevronDown className="w-3 h-3 text-zinc-600" />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="absolute top-full left-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[110px]">
                {plans.map(p => (
                  <button key={p} onClick={() => { onPlanChange(user._id, p); setMenuOpen(false); }}
                    disabled={saving === user._id}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-mono hover:bg-zinc-800 transition-colors ${user.plan === p || (!user.plan && p === 'free') ? 'text-white bg-zinc-800' : 'text-zinc-400'}`}>
                    {p === 'business' ? <Building2 className="w-3 h-3 text-purple-400" /> :
                     p === 'pro' ? <Star className="w-3 h-3 text-yellow-400" /> :
                     <Zap className="w-3 h-3 text-zinc-500" />}
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                    {saving === user._id && p !== user.plan && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {user.auth_provider === 'google'
            ? <span className="text-blue-400 font-mono text-xs flex items-center gap-1"><span className="text-sm">G</span> Google</span>
            : <span className="text-zinc-500 font-mono text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>}
          {user.email_verified && <CheckCircle className="w-3 h-3 text-emerald-500" />}
        </div>
      </td>
      <td className="px-4 py-3 text-zinc-500 font-mono text-xs">
        {user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onSelect(user)}
            className="p-1.5 text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
            title="Ver detalles">
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(user)}
            className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
            title="Eliminar usuario">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </motion.tr>
  );
}

// ─── User Detail Drawer ────────────────────────────────────────────
function UserDrawer({ user, token, onClose, onUpdate, onDelete }) {
  const [detail, setDetail]   = useState(user);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState('info');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(user.name || '');
  const [editNotes, setEditNotes] = useState(user.admin_notes || '');
  const [saving, setSaving]   = useState(false);
  const [resetUrl, setResetUrl] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/users/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDetail(r.data);
        setEditName(r.data.name || '');
        setEditNotes(r.data.admin_notes || '');
      } catch {}
      finally { setLoading(false); }
    };
    fetchDetail();
  }, [user._id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/users/${user._id}`,
        { name: editName, admin_notes: editNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetail(r.data);
      onUpdate(r.data);
      setEditing(false);
    } catch {}
    finally { setSaving(false); }
  };

  const handlePlanChange = async (plan) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/users/${user._id}`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetail(r.data);
      onUpdate(r.data);
    } catch {}
    finally { setSaving(false); }
  };

  const handleToggleVerified = async () => {
    const r = await axios.put(`${API_URL}/api/admin/users/${user._id}`,
      { email_verified: !detail.email_verified },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setDetail(r.data); onUpdate(r.data);
  };

  const handleResetPassword = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/users/${user._id}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResetUrl(r.data.reset_url);
    } catch {}
  };

  const plans = ['free', 'pro', 'business'];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-stretch" onClick={onClose}>
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <motion.div initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg bg-zinc-950 border-l border-zinc-800 flex flex-col h-full overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-mono font-black">
              {(detail?.name || 'U')[0].toUpperCase()}
            </div>
            <div>
              <p className="text-white font-mono font-bold text-sm">{detail?.name || '—'}</p>
              <p className="text-zinc-500 font-mono text-xs">{detail?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-3 border-b border-zinc-800 bg-zinc-900/50">
          {['info', 'plan', 'actions'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-1.5 rounded-lg font-mono text-xs uppercase tracking-wider transition-all ${
                tab === t ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : (
          <div className="flex-1 p-5 space-y-4">

            {/* ── INFO TAB ── */}
            {tab === 'info' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
                    <p className="font-heading text-2xl font-black text-white">{detail?.scan_count ?? '—'}</p>
                    <p className="text-zinc-600 font-mono text-xs">Scans</p>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
                    <p className={`font-heading text-lg font-black ${detail?.email_verified ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {detail?.email_verified ? '✓' : '✗'}
                    </p>
                    <p className="text-zinc-600 font-mono text-xs">Verificado</p>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
                    <PlanBadge plan={detail?.plan} />
                    <p className="text-zinc-600 font-mono text-xs mt-1">Plan</p>
                  </div>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 rounded-xl divide-y divide-zinc-800">
                  {[
                    { label: 'ID', value: detail?._id },
                    { label: 'Email', value: detail?.email },
                    { label: 'Auth', value: detail?.auth_provider || 'email' },
                    { label: 'Stripe Customer', value: detail?.stripe_customer_id || '—' },
                    { label: 'Stripe Sub', value: detail?.stripe_subscription_id || '—' },
                    { label: 'Creado', value: detail?.created_at ? new Date(detail.created_at).toLocaleString('es-ES') : '—' },
                    { label: 'Actualizado', value: detail?.updated_at ? new Date(detail.updated_at).toLocaleString('es-ES') : '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-zinc-500 font-mono text-xs uppercase tracking-wider w-28 flex-shrink-0">{label}</span>
                      <span className="text-zinc-300 font-mono text-xs text-right truncate max-w-[200px]" title={value}>{value}</span>
                    </div>
                  ))}
                </div>

                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-1.5">Nombre</label>
                      <input value={editName} onChange={e => setEditName(e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-xl px-4 py-2.5 text-zinc-200 font-mono text-sm outline-none" />
                    </div>
                    <div>
                      <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-1.5">Notas de admin</label>
                      <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3}
                        className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-xl px-4 py-2.5 text-zinc-200 font-mono text-sm outline-none resize-none" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSave} disabled={saving}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs rounded-xl">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
                      </button>
                      <button onClick={() => setEditing(false)} className="px-4 py-2 bg-zinc-800 text-zinc-400 font-mono text-xs rounded-xl hover:bg-zinc-700">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {detail?.admin_notes && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 mb-3">
                        <p className="text-zinc-500 font-mono text-xs uppercase tracking-wider mb-1">Notas de admin</p>
                        <p className="text-amber-300 font-mono text-xs">{detail.admin_notes}</p>
                      </div>
                    )}
                    <button onClick={() => setEditing(true)}
                      className="flex items-center gap-2 text-zinc-500 hover:text-blue-400 font-mono text-xs transition-colors">
                      <Edit3 className="w-3.5 h-3.5" /> Editar nombre / notas
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── PLAN TAB ── */}
            {tab === 'plan' && (
              <div className="space-y-3">
                <p className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Cambiar plan</p>
                {plans.map(p => (
                  <button key={p} onClick={() => handlePlanChange(p)} disabled={saving}
                    className={`w-full flex items-center gap-4 p-4 border rounded-xl transition-all ${
                      (detail?.plan || 'free') === p
                        ? p === 'business' ? 'bg-purple-500/10 border-purple-500/40' :
                          p === 'pro' ? 'bg-yellow-500/10 border-yellow-500/40' :
                          'bg-zinc-800 border-zinc-600'
                        : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'
                    }`}>
                    {p === 'business' ? <Building2 className="w-5 h-5 text-purple-400" /> :
                     p === 'pro' ? <Star className="w-5 h-5 text-yellow-400" /> :
                     <Zap className="w-5 h-5 text-zinc-500" />}
                    <div className="flex-1 text-left">
                      <p className="text-white font-mono text-sm font-bold">{p.charAt(0).toUpperCase() + p.slice(1)}</p>
                      <p className="text-zinc-500 font-mono text-xs">
                        {p === 'business' ? '50 lotes · 5.000 API · 5 seats' :
                         p === 'pro' ? '10 lotes · 500 API · ilimitado' :
                         '5 análisis/día · sin API'}
                      </p>
                    </div>
                    {(detail?.plan || 'free') === p && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    {saving && (detail?.plan || 'free') !== p && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
                  </button>
                ))}
              </div>
            )}

            {/* ── ACTIONS TAB ── */}
            {tab === 'actions' && (
              <div className="space-y-3">
                <button onClick={handleToggleVerified}
                  className={`w-full flex items-center gap-3 p-4 border rounded-xl transition-all ${
                    detail?.email_verified
                      ? 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40'
                      : 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40'
                  }`}>
                  {detail?.email_verified
                    ? <><XCircle className="w-5 h-5 text-amber-400" /><div className="text-left"><p className="text-white font-mono text-sm font-bold">Marcar como no verificado</p><p className="text-zinc-500 font-mono text-xs">Revocar verificación de email</p></div></>
                    : <><CheckCircle className="w-5 h-5 text-emerald-400" /><div className="text-left"><p className="text-white font-mono text-sm font-bold">Marcar como verificado</p><p className="text-zinc-500 font-mono text-xs">Verificar email manualmente</p></div></>
                  }
                </button>

                <button onClick={handleResetPassword}
                  className="w-full flex items-center gap-3 p-4 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-xl transition-all">
                  <Key className="w-5 h-5 text-blue-400" />
                  <div className="text-left">
                    <p className="text-white font-mono text-sm font-bold">Generar link de reset</p>
                    <p className="text-zinc-500 font-mono text-xs">Token válido 24h para resetear contraseña</p>
                  </div>
                </button>

                {resetUrl && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
                    <p className="text-blue-300 font-mono text-xs mb-2 font-bold">Link generado (24h):</p>
                    <div className="flex items-center gap-2">
                      <p className="text-zinc-400 font-mono text-xs truncate flex-1">{resetUrl}</p>
                      <button onClick={() => navigator.clipboard.writeText(resetUrl)}
                        className="text-zinc-500 hover:text-white flex-shrink-0">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t border-zinc-800">
                  <button onClick={() => onDelete(detail)}
                    className="w-full flex items-center gap-3 p-4 bg-red-500/5 border border-red-500/20 hover:border-red-500/40 rounded-xl transition-all">
                    <Trash2 className="w-5 h-5 text-red-400" />
                    <div className="text-left">
                      <p className="text-red-300 font-mono text-sm font-bold">Eliminar usuario</p>
                      <p className="text-zinc-500 font-mono text-xs">Acción irreversible — elimina todos sus datos</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Delete Confirm Modal ──────────────────────────────────────────
function DeleteModal({ user, onConfirm, onCancel, loading }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] flex items-center justify-center p-4" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={e => e.stopPropagation()}
        className="relative bg-zinc-900 border border-red-500/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-6 h-6 text-red-400" />
        </div>
        <h2 className="text-white font-heading font-black text-lg text-center mb-2">Eliminar usuario</h2>
        <p className="text-zinc-400 font-mono text-sm text-center mb-1">¿Eliminar a <span className="text-white">{user?.name}</span>?</p>
        <p className="text-zinc-600 font-mono text-xs text-center mb-6">Esta acción eliminará su cuenta y todos sus análisis permanentemente.</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-zinc-800 text-zinc-300 font-mono text-sm rounded-xl hover:bg-zinc-700 transition-colors">
            Cancelar
          </button>
          <button onClick={() => onConfirm(user)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-500 text-white font-mono text-sm rounded-xl transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Eliminar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Main Admin Panel ──────────────────────────────────────────────
export default function AdminPanel({ user: currentUser, onBack, onLogout }) {
  const [tab, setTab]         = useState('dashboard');
  const [stats, setStats]     = useState(null);
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [savingPlan, setSavingPlan] = useState(null);
  const [activity, setActivity] = useState({ recent_scans: [], recent_users: [] });
  const [toasts, setToasts]   = useState([]);
  const toastId = useRef(0);
  const searchTimeout = useRef(null);

  const token = localStorage.getItem('veritas_token');

  const toast = (msg, type = 'success') => {
    const id = ++toastId.current;
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  };

  // Access guard
  if (currentUser?.email?.toLowerCase() !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white font-heading font-black text-xl mb-2">Acceso denegado</p>
          <p className="text-zinc-500 font-mono text-sm">Esta página es solo para administradores.</p>
          <button onClick={onBack} className="mt-6 px-5 py-2.5 bg-zinc-800 text-zinc-300 font-mono text-sm rounded-xl hover:bg-zinc-700 transition-colors">
            Volver
          </button>
        </div>
      </div>
    );
  }

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(r.data);
    } catch (e) { toast('Error cargando estadísticas', 'error'); }
  }, [token]);

  const fetchUsers = useCallback(async (p = 1, q = search, plan = planFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (q) params.set('q', q);
      if (plan && plan !== 'all') params.set('plan', plan);
      const r = await axios.get(`${API_URL}/api/admin/users?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(r.data.users);
      setTotal(r.data.total);
      setPages(r.data.pages);
      setPage(p);
    } catch { toast('Error cargando usuarios', 'error'); }
    finally { setLoading(false); }
  }, [token, search, planFilter]);

  const fetchActivity = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/recent-activity`, { headers: { Authorization: `Bearer ${token}` } });
      setActivity(r.data);
    } catch {}
  }, [token]);

  useEffect(() => { fetchStats(); fetchUsers(1); fetchActivity(); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchUsers(1, val, planFilter), 400);
  };

  const handlePlanChange = async (userId, plan) => {
    setSavingPlan(userId);
    try {
      const r = await axios.put(`${API_URL}/api/admin/users/${userId}`, { plan }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, plan: r.data.plan } : u));
      toast(`Plan cambiado a ${plan}`);
    } catch { toast('Error al cambiar plan', 'error'); }
    finally { setSavingPlan(null); }
  };

  const handleDelete = async (user) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${user._id}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.filter(u => u._id !== user._id));
      setTotal(t => t - 1);
      setDeleteTarget(null);
      setSelectedUser(null);
      toast(`Usuario ${user.name} eliminado`);
      fetchStats();
    } catch (e) { toast(e.response?.data?.detail || 'Error al eliminar', 'error'); }
    finally { setDeleting(false); }
  };

  const handleUserUpdate = (updated) => {
    setUsers(prev => prev.map(u => u._id === updated._id ? { ...u, ...updated } : u));
    toast('Usuario actualizado');
  };

  // ─── Auto Blog state ───────────────────────────────────────────
  const [autoSettings, setAutoSettings] = useState(null);
  const [autoLog, setAutoLog]           = useState([]);
  const [autoRunning, setAutoRunning]   = useState(false);
  const [autoSaving, setAutoSaving]     = useState(false);

  const fetchAutoSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/blog/auto-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setAutoSettings(r.data);
    } catch {}
  }, [token]);

  const fetchAutoLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/blog/auto-log?limit=15`, { headers: { Authorization: `Bearer ${token}` } });
      setAutoLog(r.data.logs || []);
    } catch {}
  }, [token]);

  const saveAutoSettings = async () => {
    setAutoSaving(true);
    try {
      await axios.put(`${API_URL}/api/blog/auto-settings`, autoSettings, { headers: { Authorization: `Bearer ${token}` } });
      toast('Ajustes guardados');
    } catch { toast('Error guardando ajustes', 'error'); }
    finally { setAutoSaving(false); }
  };

  const triggerAutoPublish = async () => {
    setAutoRunning(true);
    try {
      await axios.post(`${API_URL}/api/blog/auto-publish`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast('Auto-publish iniciado — revisa el log en ~1 min');
      setTimeout(() => { fetchAutoLog(); setAutoRunning(false); }, 8000);
    } catch (e) { toast(e.response?.data?.detail || 'Error', 'error'); setAutoRunning(false); }
  };

  useEffect(() => {
    if (tab === 'autoblog') { fetchAutoSettings(); fetchAutoLog(); }
  }, [tab]);

  // ─── Module config state ─────────────────────────────────────────
  const [moduleConfig, setModuleConfig] = useState(null);
  const [moduleSaving, setModuleSaving] = useState(false);

  const fetchModuleConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/modules/config`, { headers: { Authorization: `Bearer ${token}` } });
      setModuleConfig(r.data);
    } catch { toast('Error cargando config de módulos', 'error'); }
  }, [token]);

  const saveModuleConfig = async () => {
    setModuleSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/modules/config`, moduleConfig, { headers: { Authorization: `Bearer ${token}` } });
      toast('Configuración guardada');
    } catch { toast('Error guardando', 'error'); }
    finally { setModuleSaving(false); }
  };

  useEffect(() => {
    if (tab === 'modules') fetchModuleConfig();
  }, [tab]);

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart2 },
    { id: 'users',     label: 'Usuarios',  icon: Users },
    { id: 'activity',  label: 'Actividad', icon: Activity },
    { id: 'autoblog',  label: 'Auto Blog', icon: Calendar },
    { id: 'modules',   label: 'Módulos',   icon: Lock },
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(ellipse, #ef4444, transparent 70%)' }} />
      </div>

      <Toast toasts={toasts} />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-zinc-800/80 bg-zinc-950/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <button onClick={onBack} className="text-zinc-500 hover:text-white font-mono text-xs transition-colors flex items-center gap-1.5">
            ← Volver
          </button>
          <div className="flex items-center gap-2">
            <Scan className="w-4 h-4 text-red-500" />
            <span className="font-heading text-sm font-black">VERITAS <span className="text-red-500">ADMIN</span></span>
          </div>
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 font-mono text-xs font-bold">
            <Shield className="w-3 h-3" /> Admin
          </span>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={fetchStats} className="p-2 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-all" title="Refrescar">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={onLogout}
              className="flex items-center gap-1.5 text-zinc-500 hover:text-red-400 font-mono text-xs transition-colors px-3 py-1.5 rounded-lg border border-zinc-800 hover:border-red-500/30">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tab nav */}
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 mb-6 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-wider transition-all ${
                tab === id ? 'bg-zinc-800 text-white border border-zinc-700' : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* ════ DASHBOARD ════ */}
        {tab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard label="Total Usuarios" value={stats?.total_users ?? '—'} icon={Users} color="blue" trend={stats?.new_users_week} />
              <StatCard label="Plan Pro" value={stats?.pro_users ?? '—'} icon={Star} color="amber" />
              <StatCard label="Plan Business" value={stats?.business_users ?? '—'} icon={Building2} color="purple" />
              <StatCard label="Scans Hoy" value={stats?.scans_today ?? '—'} icon={Activity} color="emerald" />
              <StatCard label="MRR Estimado" value={stats ? `€${stats.estimated_mrr}` : '—'} icon={TrendingUp} color="zinc" sub="Pro×9 + Biz×29" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Total Scans" value={stats?.total_scans ?? '—'} icon={Scan} color="blue" />
              <StatCard label="Verificados" value={stats?.verified_users ?? '—'} icon={UserCheck} color="emerald" />
              <StatCard label="Google OAuth" value={stats?.google_users ?? '—'} icon={Key} color="zinc" />
              <StatCard label="Nuevos (7d)" value={stats?.new_users_week ?? '—'} icon={ArrowUpRight} color="purple" />
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
                <h2 className="font-heading font-bold text-white text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" /> Usuarios recientes
                </h2>
                <button onClick={() => setTab('users')} className="text-zinc-500 hover:text-blue-400 font-mono text-xs transition-colors flex items-center gap-1">
                  Ver todos <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {activity.recent_users.slice(0, 5).map(u => (
                  <div key={u._id} className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-800/30 transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-mono text-xs font-bold">
                      {(u.name || 'U')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-mono text-sm truncate">{u.name}</p>
                      <p className="text-zinc-500 font-mono text-xs truncate">{u.email}</p>
                    </div>
                    <PlanBadge plan={u.plan} />
                    <p className="text-zinc-600 font-mono text-xs hidden sm:block">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════ USERS ════ */}
        {tab === 'users' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Buscar por nombre, email o ID..."
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-blue-500 rounded-xl pl-10 pr-4 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                />
                {search && (
                  <button onClick={() => { setSearch(''); fetchUsers(1, '', planFilter); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {['all', 'free', 'pro', 'business'].map(p => (
                  <button key={p} onClick={() => { setPlanFilter(p); fetchUsers(1, search, p); }}
                    className={`px-3 py-2.5 rounded-xl font-mono text-xs uppercase tracking-wider border transition-all ${
                      planFilter === p ? 'bg-zinc-800 text-white border-zinc-600' : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:border-zinc-600'
                    }`}>
                    {p}
                  </button>
                ))}
              </div>
              <button onClick={() => fetchUsers(page)} className="p-2.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-500 hover:text-white rounded-xl transition-all">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <p className="text-zinc-500 font-mono text-xs">{total} usuarios · página {page} de {pages}</p>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      {['Usuario', 'Plan', 'Auth', 'Registro', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-zinc-500 font-mono text-xs uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="text-center py-12">
                        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" />
                      </td></tr>
                    ) : users.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-12 text-zinc-600 font-mono text-sm">No se encontraron usuarios</td></tr>
                    ) : users.map(u => (
                      <UserRow key={u._id} user={u}
                        onSelect={setSelectedUser}
                        onPlanChange={handlePlanChange}
                        onDelete={setDeleteTarget}
                        saving={savingPlan}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-zinc-800">
                  <button onClick={() => fetchUsers(page - 1)} disabled={page <= 1}
                    className="p-2 text-zinc-500 hover:text-white disabled:opacity-30 hover:bg-zinc-800 rounded-lg transition-all">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => fetchUsers(p)}
                      className={`w-8 h-8 rounded-lg font-mono text-xs transition-all ${
                        page === p ? 'bg-blue-600 text-white' : 'text-zinc-500 hover:bg-zinc-800 hover:text-white'
                      }`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => fetchUsers(page + 1)} disabled={page >= pages}
                    className="p-2 text-zinc-500 hover:text-white disabled:opacity-30 hover:bg-zinc-800 rounded-lg transition-all">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ AUTO BLOG ════ */}
        {tab === 'autoblog' && (
          <div className="space-y-6">
            {!autoSettings ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-white font-heading font-bold text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-teal-400" /> Auto Blog Publisher
                    </h2>
                    <p className="text-zinc-500 font-mono text-xs mt-1">Genera posts en borrador automáticamente cada día con noticias de IA</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={triggerAutoPublish} disabled={autoRunning}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono text-xs rounded-xl transition-all">
                      {autoRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                      Generar ahora
                    </button>
                    <button onClick={saveAutoSettings} disabled={autoSaving}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-mono text-xs rounded-xl transition-all">
                      {autoSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Guardar
                    </button>
                  </div>
                </div>

                {/* Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Enabled */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Estado</label>
                    <button onClick={() => setAutoSettings(s => ({...s, enabled: !s.enabled}))}
                      className={`flex items-center gap-3 w-full p-3 rounded-xl border transition-all ${
                        autoSettings.enabled ? 'bg-teal-500/10 border-teal-500/30 text-teal-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                      }`}>
                      <div className={`w-10 h-6 rounded-full relative transition-all ${autoSettings.enabled ? 'bg-teal-500' : 'bg-zinc-600'}`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${autoSettings.enabled ? 'left-5' : 'left-1'}`} />
                      </div>
                      <span className="font-mono text-sm">{autoSettings.enabled ? 'Activado' : 'Desactivado'}</span>
                    </button>
                  </div>

                  {/* Posts per day */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Posts por día</label>
                    <div className="flex gap-2">
                      {[1, 2].map(n => (
                        <button key={n} onClick={() => setAutoSettings(s => ({...s, posts_per_day: n}))}
                          className={`flex-1 py-3 rounded-xl border font-mono text-sm font-bold transition-all ${
                            autoSettings.posts_per_day === n ? 'bg-blue-600 border-blue-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                          }`}>
                          {n} {n === 1 ? 'post' : 'posts'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hour */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Hora de publicación (UTC)</label>
                    <select value={autoSettings.publish_hour_utc}
                      onChange={e => setAutoSettings(s => ({...s, publish_hour_utc: parseInt(e.target.value)}))}
                      className="w-full bg-zinc-800 border border-zinc-700 text-zinc-200 font-mono text-sm rounded-xl px-4 py-3 outline-none">
                      {Array.from({length: 24}, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2,'0')}:00 UTC</option>
                      ))}
                    </select>
                  </div>

                  {/* Language */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Idioma</label>
                    <div className="flex gap-2">
                      {[['es', 'Español'], ['en', 'English']].map(([v, l]) => (
                        <button key={v} onClick={() => setAutoSettings(s => ({...s, language: v}))}
                          className={`flex-1 py-3 rounded-xl border font-mono text-sm transition-all ${
                            autoSettings.language === v ? 'bg-blue-600 border-blue-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                          }`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* YouTube */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Vídeos de YouTube</label>
                    <button onClick={() => setAutoSettings(s => ({...s, include_youtube: !s.include_youtube}))}
                      className={`flex items-center gap-3 w-full p-3 rounded-xl border transition-all ${
                        autoSettings.include_youtube ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                      }`}>
                      <div className={`w-10 h-6 rounded-full relative transition-all ${autoSettings.include_youtube ? 'bg-red-500' : 'bg-zinc-600'}`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${autoSettings.include_youtube ? 'left-5' : 'left-1'}`} />
                      </div>
                      <span className="font-mono text-sm">{autoSettings.include_youtube ? 'Incluir vídeos' : 'Sin vídeos'}</span>
                    </button>
                    {autoSettings.include_youtube && (
                      <p className="text-zinc-600 font-mono text-xs mt-2">⚠️ Requiere <code className="text-amber-400">YOUTUBE_API_KEY</code> en Railway</p>
                    )}
                  </div>

                  {/* Topics */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 sm:col-span-2">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider block mb-3">Tópicos (separados por coma)</label>
                    <textarea
                      value={autoSettings.topics?.join(', ')}
                      onChange={e => setAutoSettings(s => ({...s, topics: e.target.value.split(',').map(t => t.trim()).filter(Boolean)}))}
                      rows={2}
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-xl px-4 py-3 text-zinc-200 font-mono text-sm outline-none resize-none"
                    />
                  </div>
                </div>

                {/* Log */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
                    <h3 className="font-heading font-bold text-white text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4 text-teal-400" /> Log de generaciones
                    </h3>
                    <button onClick={fetchAutoLog} className="text-zinc-500 hover:text-white">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {autoLog.length === 0 ? (
                    <p className="text-center py-10 text-zinc-600 font-mono text-sm">Sin registros aún. Pulsa &quot;Generar ahora&quot; para probar.</p>
                  ) : (
                    <div className="divide-y divide-zinc-800/50">
                      {autoLog.map((log, i) => (
                        <div key={i} className="flex items-start gap-3 px-5 py-3">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.status === 'success' ? 'bg-teal-400' : 'bg-red-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-mono text-sm truncate">{log.title || log.message || '—'}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-zinc-600 font-mono text-xs">{new Date(log.created_at).toLocaleString('es-ES')}</span>
                              {log.slug && <span className="text-zinc-600 font-mono text-xs">/blog/{log.slug}</span>}
                              {log.youtube && log.youtube !== 'none' && <span className="text-red-400 font-mono text-xs">▶ YouTube</span>}
                            </div>
                          </div>
                          <span className={`font-mono text-xs px-2 py-0.5 rounded-full ${
                            log.status === 'success' ? 'bg-teal-500/10 text-teal-400' : 'bg-red-500/10 text-red-400'
                          }`}>{log.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ════ MÓDULOS ════ */}
        {tab === 'modules' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-heading font-bold text-base flex items-center gap-2">
                  <Lock className="w-4 h-4 text-orange-400" /> Control de Módulos
                </h2>
                <p className="text-zinc-500 font-mono text-xs mt-1">Activa o desactiva el acceso a Veritas Image y Veritas Video para todos los usuarios</p>
              </div>
              <button onClick={saveModuleConfig} disabled={moduleSaving || !moduleConfig}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-mono text-xs rounded-xl transition-all">
                {moduleSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar cambios
              </button>
            </div>

            {!moduleConfig ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* VERITAS IMAGE */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                        <span className="text-xl">🖼️</span>
                      </div>
                      <div>
                        <p className="text-white font-heading font-bold text-sm">Veritas Image</p>
                        <p className="text-zinc-500 font-mono text-xs">Generador de imágenes con IA</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setModuleConfig(c => ({ ...c, image_enabled: !c.image_enabled }))}
                      className={`relative w-12 h-7 rounded-full transition-all ${
                        moduleConfig.image_enabled ? 'bg-purple-500' : 'bg-zinc-700'
                      }`}>
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        moduleConfig.image_enabled ? 'left-6' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-mono ${
                    moduleConfig.image_enabled
                      ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400'
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}>
                    {moduleConfig.image_enabled ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {moduleConfig.image_enabled ? 'Acceso público activo' : 'Acceso bloqueado — invisible para usuarios'}
                  </div>

                  <div className="space-y-1">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider">Mensaje al bloquear</label>
                    <input
                      value={moduleConfig.image_message || ''}
                      onChange={e => setModuleConfig(c => ({ ...c, image_message: e.target.value }))}
                      placeholder="Veritas Image no está disponible temporalmente."
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-purple-500 rounded-xl px-4 py-2.5 text-zinc-200 font-mono text-xs outline-none transition-colors"
                    />
                  </div>
                </div>

                {/* VERITAS CONTENT */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center">
                        <span className="text-xl">✦</span>
                      </div>
                      <div>
                        <p className="text-white font-heading font-bold text-sm">Veritas Content</p>
                        <p className="text-zinc-500 font-mono text-xs">Red social de creaciones IA</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setModuleConfig(c => ({ ...c, content_enabled: !c.content_enabled }))}
                      className={`relative w-12 h-7 rounded-full transition-all ${
                        moduleConfig.content_enabled !== false ? 'bg-pink-500' : 'bg-zinc-700'
                      }`}>
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        moduleConfig.content_enabled !== false ? 'left-6' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-mono ${
                    moduleConfig.content_enabled !== false
                      ? 'bg-pink-500/10 border border-pink-500/20 text-pink-400'
                      : 'bg-zinc-800 border border-zinc-700 text-zinc-500'
                  }`}>
                    {moduleConfig.content_enabled !== false ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {moduleConfig.content_enabled !== false ? 'Acceso público activo' : 'Acceso bloqueado — invisible para usuarios'}
                  </div>
                </div>

                {/* VERITAS VIDEO */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                        <span className="text-xl">🎬</span>
                      </div>
                      <div>
                        <p className="text-white font-heading font-bold text-sm">Veritas Video</p>
                        <p className="text-zinc-500 font-mono text-xs">Generador de vídeos con IA</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setModuleConfig(c => ({ ...c, video_enabled: !c.video_enabled }))}
                      className={`relative w-12 h-7 rounded-full transition-all ${
                        moduleConfig.video_enabled ? 'bg-red-500' : 'bg-zinc-700'
                      }`}>
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        moduleConfig.video_enabled ? 'left-6' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-mono ${
                    moduleConfig.video_enabled
                      ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                      : 'bg-zinc-800 border border-zinc-700 text-zinc-500'
                  }`}>
                    {moduleConfig.video_enabled ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {moduleConfig.video_enabled ? 'Acceso público activo' : 'Acceso bloqueado — invisible para usuarios'}
                  </div>

                  <div className="space-y-1">
                    <label className="text-zinc-500 font-mono text-xs uppercase tracking-wider">Mensaje al bloquear</label>
                    <input
                      value={moduleConfig.video_message || ''}
                      onChange={e => setModuleConfig(c => ({ ...c, video_message: e.target.value }))}
                      placeholder="Veritas Video no está disponible temporalmente."
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-red-500 rounded-xl px-4 py-2.5 text-zinc-200 font-mono text-xs outline-none transition-colors"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="sm:col-span-2 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                    <div className="text-zinc-500 font-mono text-xs space-y-1">
                      <p>Al <strong className="text-zinc-300">desactivar</strong> un módulo:</p>
                      <p>• La card desaparece de la Landing Page automáticamente</p>
                      <p>• Si alguien intenta acceder por URL, se redirige al inicio</p>
                      <p>• Las llamadas API devuelven error 503 con el mensaje configurado</p>
                      <p>• Los cambios son instantáneos sin necesidad de redeploy</p>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ════ ACTIVITY ════ */}
        {tab === 'activity' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-heading font-bold text-base">Actividad reciente</h2>
              <button onClick={fetchActivity} className="flex items-center gap-1.5 text-zinc-500 hover:text-white font-mono text-xs transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Actualizar
              </button>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">  
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <h3 className="font-heading font-bold text-white text-sm">Registros recientes</h3>
                </div>
                <div className="divide-y divide-zinc-800/50">
                  {activity.recent_users.length === 0 ? (
                    <p className="text-center py-8 text-zinc-600 font-mono text-sm">Sin usuarios</p>
                  ) : activity.recent_users.map(u => (
                    <div key={u._id} className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                      onClick={() => { setSelectedUser(u); setTab('users'); }}>
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-mono text-xs font-bold flex-shrink-0">
                        {(u.name || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-mono text-sm truncate">{u.name}</p>
                        <p className="text-zinc-500 font-mono text-xs truncate">{u.email}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <PlanBadge plan={u.plan} />
                        <p className="text-zinc-600 font-mono text-xs mt-1">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedUser && (
          <UserDrawer user={selectedUser} token={token} onClose={() => setSelectedUser(null)}
            onUpdate={handleUserUpdate} onDelete={(u) => { setDeleteTarget(u); setSelectedUser(null); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteTarget && (
          <DeleteModal user={deleteTarget} onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)} loading={deleting} />
        )}
      </AnimatePresence>
    </div>
  );
}
